'use client';

import React, { memo, useMemo, useState, useEffect } from 'react';
import AppIcon from './AppIcon';
import AppImage from './AppImage';

interface AppLogoProps {
  src?: string; // Image source (optional)
  iconName?: string; // Icon name when no image
  size?: number; // Size for icon/image
  className?: string; // Additional classes
  onClick?: () => void; // Click handler
}

const AppLogo = memo(function AppLogo({
  src = '/assets/images/app_logo.png',
  iconName = 'SparklesIcon',
  size = 64,
  className = '',
  onClick,
}: AppLogoProps) {
  const [logoSrc, setLogoSrc] = useState(src);

  useEffect(() => {
    const stored = localStorage.getItem('portalLogo');
    if (stored) setLogoSrc(stored);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'portalLogo') {
        setLogoSrc(e.newValue || src);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [src]);

  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  return (
    <div className={containerClassName} onClick={onClick}>
      {logoSrc ? (
        logoSrc.startsWith('data:') ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={logoSrc}
            alt="Portal logo"
            width={size}
            height={size}
            className="flex-shrink-0"
            style={{ width: size, height: size, objectFit: 'contain' }}
          />
        ) : (
          <AppImage
            src={logoSrc}
            alt="Logo"
            width={size}
            height={size}
            className="flex-shrink-0"
            priority={true}
            unoptimized={logoSrc.endsWith('.svg')}
          />
        )
      ) : (
        <AppIcon name={iconName} size={size} className="flex-shrink-0" />
      )}
    </div>
  );
});

export default AppLogo;
